import { useMemo, useState } from 'react';
import {
  Heading,
  Text,
  Flex,
  Badge,
  Tooltip,
  Icon,
  IconButton,
  Checkbox,
  Separator,
  TextField,
  Skeleton,
  SegmentedControl,
  Card,
} from '@scale/scaleui-radix';
import { faTrash, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { AuditRubricCriterion } from '@scale/shared-types';
import { TaskType } from '@scale/shared/defaults';
import {
  MultiTurnInteractionConfigurationV2,
  InteractionStepType,
  ChatResponseV2,
  ThreadedChatResponse,
} from '@scale/shared/types/chat';

export const FillCriterionSteps = ({
  criterion,
  response,
}: {
  criterion: AuditRubricCriterion;
  response: {
    type: TaskType;
    response: ThreadedChatResponse | ChatResponseV2;
  };
}) => {
  const responseSteps = useMemo(() => {
    return response.type === TaskType.Chat
      ? (response.response as ThreadedChatResponse).responses.map(response => response.step_id)
      : [
          (response.response as ChatResponseV2).before,
          ...(response.response as ChatResponseV2).turns,
          (response.response as ChatResponseV2).after,
        ].flatMap(turnResponse =>
          // todo: make sure to filter out steps that don't have a response
          Object.keys(turnResponse),
        );
  }, [response]);
  const unavailableStepIds = useMemo(() => {
    return new Set(
      criterion.steps.map(step => step.id).filter(stepId => !responseSteps.includes(stepId)),
    );
  }, [criterion.steps, responseSteps]);
  return (
    <Flex direction="column" gap="2">
      <Text weight="medium">{`Steps applicable (${
        criterion.useAllSteps ? 'all' : criterion.steps.length
      })`}</Text>
      <Separator orientation="horizontal" size="4" />
      {criterion.steps.length > 0 ? (
        criterion.steps.map((step, index) => (
          <>
            <Flex direction="row" justify="between" key={step.id}>
              <Text key={step.id} color={unavailableStepIds.has(step.id) ? 'neutral' : 'auto'}>
                {step.title}
              </Text>
            </Flex>
            {index < criterion.steps.length - 1 && <Separator orientation="horizontal" size="4" />}
          </>
        ))
      ) : (
        <Text>None</Text>
      )}
    </Flex>
  );
};

const ViewCriterionSteps = ({
  criterion,
  availableStepMap,
  taskParams,
  isLoadingTaskParams,
  taskTemplateError,
  chatApiRef,
}: {
  criterion: AuditRubricCriterion;
  availableStepMap: Record<
    string,
    {
      id: string;
      title: string;
      stepType: InteractionStepType;
    }
  >;
  taskParams: MultiTurnInteractionConfigurationV2 | undefined;
  isLoadingTaskParams: boolean;
  taskTemplateError: string | undefined;
  chatApiRef?: React.RefObject<{
    scrollToStep: (step: { stepId: string; stage: string; turn: number }) => void;
  }>;
}) => {
  const onCriteriaClick = (stepId: string) => {
    const turnIndex = taskParams?.turn?.findIndex(s => s.id === stepId) ?? -1;
    if (chatApiRef?.current && availableStepMap[stepId] && turnIndex >= 0) {
      chatApiRef.current.scrollToStep({
        stepId,
        stage: 'turn',
        turn: turnIndex,
      });
    }
  };

  if (criterion.useAllSteps) {
    return <Text weight="medium">Steps applicable (all)</Text>;
  }

  return (
    <Flex direction="column" gap="2">
      <Text weight="medium">{`Steps applicable (${criterion.steps.length})`}</Text>
      <Separator orientation="horizontal" size="4" />
      {isLoadingTaskParams ? (
        <Skeleton></Skeleton>
      ) : taskParams ? (
        criterion.steps.length > 0 ? (
          criterion.steps.map((step, index) => (
            <>
              <Flex direction="row" justify="between" key={step.id}>
                <Text
                  key={step.id}
                  onClick={() => onCriteriaClick(step.id)}
                  className={availableStepMap[step.id] ? 'underline' : undefined}
                  style={{ cursor: availableStepMap[step.id] ? 'pointer' : 'default' }}
                >
                  {step.title}
                </Text>
                {!availableStepMap[step.id] && (
                  <Tooltip content="This step is not available in the current taxonomy version">
                    <Badge variant="outline" color="error">
                      Out of date
                    </Badge>
                  </Tooltip>
                )}
              </Flex>
              {index < criterion.steps.length - 1 && (
                <Separator orientation="horizontal" size="4" />
              )}
            </>
          ))
        ) : (
          <Text>None</Text>
        )
      ) : (
        <Text color="neutral" className="italic">
          {`Cannot choose steps for this taxonomy version: ${taskTemplateError}`}
        </Text>
      )}
    </Flex>
  );
};

const EditCriterionSteps = ({
  criterion,
  setCriterion,
  availableSteps,
  availableStepMap,
  taskParams,
  isLoadingTaskParams,
  taskTemplateError,
  error,
}: {
  criterion: AuditRubricCriterion;
  setCriterion: (criterion: AuditRubricCriterion) => void;
  availableSteps: {
    id: string;
    title: string;
    stepType: InteractionStepType;
  }[];
  availableStepMap: Record<
    string,
    {
      id: string;
      title: string;
      stepType: InteractionStepType;
    }
  >;
  taskParams: MultiTurnInteractionConfigurationV2 | undefined;
  isLoadingTaskParams: boolean;
  taskTemplateError: string | undefined;
  error?: string;
}) => {
  const setUseAllSteps = (useAllSteps: boolean) => setCriterion({ ...criterion, useAllSteps });

  const [stepFilter, setStepFilter] = useState<string>('');

  const selectedStepIds = useMemo(() => {
    return new Set(criterion.steps.map(step => step.id));
  }, [criterion.steps]);

  const availableUnselectedSteps = useMemo(() => {
    return availableSteps?.filter(step => !selectedStepIds.has(step.id));
  }, [availableSteps, selectedStepIds]);

  const filteredSteps = useMemo(() => {
    if (!stepFilter.trim()) {
      return [...criterion.steps, ...availableUnselectedSteps];
    }
    const lowerFilter = stepFilter.toLowerCase();
    const allSteps = [...criterion.steps, ...availableUnselectedSteps];
    return allSteps.filter(
      step =>
        step.title.toLowerCase().includes(lowerFilter) ||
        step.id.toLowerCase().includes(lowerFilter),
    );
  }, [criterion.steps, availableUnselectedSteps, stepFilter]);

  const allStepsSelected = useMemo(
    () => availableUnselectedSteps.length === 0,
    [availableUnselectedSteps],
  );

  const staleSteps = useMemo(() => {
    return criterion.steps.filter(step => !availableStepMap[step.id]);
  }, [criterion.steps, availableStepMap]);

  const handleSelectAllSteps = () => {
    setCriterion({ ...criterion, steps: [...availableSteps, ...staleSteps] });
  };
  const handleDeselectAllSteps = () => {
    setCriterion({ ...criterion, steps: [] });
  };
  const handleSelectStep = (stepId: string, checked: boolean) => {
    const newSteps = checked
      ? [...criterion.steps, availableStepMap[stepId]!]
      : criterion.steps.filter(step => step.id !== stepId);
    setCriterion({ ...criterion, steps: newSteps });
  };

  const selectedSteps = (
    <Card style={error ? { boxShadow: 'inset 0 0 0 1px #dc2626' } : undefined}>
      <Flex direction="column" gap="2">
        <Flex direction="row" gap="2" align="center" width="100%">
          <TextField.Root
            value={stepFilter}
            onChange={e => setStepFilter(e.target.value)}
            className="w-full"
            placeholder="Search..."
          >
            <TextField.Slot side="left">
              <Icon icon={faSearch} label="Search" />
            </TextField.Slot>
          </TextField.Root>
        </Flex>
        {/* multiselect */}
        <Flex direction="row" gap="2">
          <Checkbox
            size="1"
            checked={allStepsSelected}
            key={'all-steps'}
            onCheckedChange={() => {
              if (!allStepsSelected) {
                handleSelectAllSteps();
              } else {
                handleDeselectAllSteps();
              }
            }}
          />
          <Text size="1" color="neutral">
            Select all
          </Text>
        </Flex>
        <Separator orientation="horizontal" size="4" />
        <Flex direction="column" gap="2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {filteredSteps?.map(step => (
            <Flex direction="row" gap="2" key={step.id}>
              <Checkbox
                size="1"
                checked={selectedStepIds.has(step.id)}
                onCheckedChange={(checked: boolean) => handleSelectStep(step.id, checked)}
              />
              <Text color="neutral">
                {step.title} ({step.id})
              </Text>
            </Flex>
          ))}
          {/* list of stale steps */}
          {staleSteps.length > 0 && (
            <>
              Stale steps:
              {staleSteps?.map(step => (
                <Flex direction="row" key={step.id}>
                  <Text color="neutral">
                    {step.title}-{step.id}
                  </Text>
                  <IconButton onClick={() => handleSelectStep(step.id, false)}>
                    <Icon icon={faTrash} label="Delete" size="1" />
                  </IconButton>
                </Flex>
              ))}
            </>
          )}
        </Flex>
      </Flex>
    </Card>
  );
  const allSteps = (
    <Text size="1" color="neutral">
      This criterion will be evaluated on the task as whole, regardless of taxonomy changes.
    </Text>
  );

  const editingSteps = (
    <>
      <SegmentedControl.Root
        value={criterion.useAllSteps ? 'all-steps' : 'selected-steps'}
        style={{ width: '100%' }}
      >
        <SegmentedControl.Item value="all-steps" onClick={() => setUseAllSteps(true)}>
          All steps
        </SegmentedControl.Item>
        <SegmentedControl.Item value="selected-steps" onClick={() => setUseAllSteps(false)}>
          Selected steps
        </SegmentedControl.Item>
      </SegmentedControl.Root>
      {criterion.useAllSteps ? allSteps : selectedSteps}
    </>
  );

  return (
    <Flex direction="column" gap="2">
      <Heading size="2">Applicable steps</Heading>
      {isLoadingTaskParams ? (
        <Skeleton></Skeleton>
      ) : taskParams ? (
        editingSteps
      ) : (
        <Text color="neutral" className="italic">
          {`Cannot choose steps for this taxonomy version: ${taskTemplateError}`}
        </Text>
      )}
      {error && (
        <Text size="2" color="red">
          {error}
        </Text>
      )}
    </Flex>
  );
};

export const CriterionSteps = ({
  criterion,
  setCriterion,
  isEditingCriterion,
  taskParams,
  isLoadingTaskParams,
  taskTemplateError,
  error,
  chatApiRef,
}: {
  criterion: AuditRubricCriterion;
  setCriterion: (criterion: AuditRubricCriterion) => void;
  isEditingCriterion: boolean;
  taskParams: MultiTurnInteractionConfigurationV2 | undefined;
  isLoadingTaskParams: boolean;
  taskTemplateError: string | undefined;
  error?: string;
  chatApiRef?: React.RefObject<{
    scrollToStep: (step: { stepId: string; stage: string; turn: number }) => void;
  }>;
}) => {
  const availableSteps = useMemo(() => {
    const steps = [
      ...(taskParams?.before ?? []),
      ...(taskParams?.turn ?? []),
      ...(taskParams?.after ?? []),
    ];
    return (
      steps.map(step => ({
        id: step.id,
        title: step.title ?? '',
        stepType: step.type,
      })) ?? []
    );
  }, [taskParams]);
  const availableStepMap = useMemo(() => {
    return Object.fromEntries(availableSteps?.map(step => [step.id, step]));
  }, [availableSteps]);

  return isEditingCriterion ? (
    <EditCriterionSteps
      criterion={criterion}
      setCriterion={setCriterion}
      availableSteps={availableSteps}
      availableStepMap={availableStepMap}
      taskParams={taskParams}
      isLoadingTaskParams={isLoadingTaskParams}
      taskTemplateError={taskTemplateError}
      error={error}
    />
  ) : (
    <ViewCriterionSteps
      criterion={criterion}
      availableStepMap={availableStepMap}
      taskParams={taskParams}
      isLoadingTaskParams={isLoadingTaskParams}
      taskTemplateError={taskTemplateError}
      chatApiRef={chatApiRef}
    />
  );
};
